import { useEffect, useState } from "react";
import { ListChecks, MessagesSquare, UsersRound } from "lucide-react";
import { Card } from "@/components/ui/card";
import { usePermissions } from "@/hooks/use-permissions";
import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";
import { getChatsUnreadThreadsCount, subscribeChatsUnread } from "@/lib/stores/chats-unread-store";
import { hasPermission } from "@/lib/stores/permissions-store";
import { getTaskCounts, subscribeTaskCounts } from "@/lib/stores/task-counts-store";
import { getThreadsNeedsCount, subscribeThreadsNeedsCount } from "@/lib/stores/threads-overview-store";
import { setActiveWorkspacePage } from "@/lib/stores/workspace-store";

// Owner guard shared by every KPI subscription: the profile-switch event fires while the
// outgoing pane is still visible+subscribed, and its numbers must not repaint with the
// incoming user's counts (workspace-owner-context contract).
function ownerMatches(ownerUserId) {
  return String(getAuthUser()?.id || "").trim() === String(ownerUserId || "").trim();
}

function KpiItem({ icon: Icon, label, count, alert, page }) {
  return (
    <button
      type="button"
      onClick={() => setActiveWorkspacePage(page)}
      className="group flex cursor-pointer items-center gap-3 text-left"
      title={`Open ${page}`}
    >
      <div
        className={cn(
          "grid h-10 w-10 flex-none place-items-center rounded-xl transition-colors duration-150",
          alert ? "bg-rose-600/14 text-rose-600 dark:text-rose-400" : "bg-primary/13 text-primary",
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div
          className={cn(
            "text-[23px] font-black leading-none tabular-nums transition-colors duration-150",
            alert ? "text-rose-600 dark:text-rose-400" : "group-hover:text-primary",
          )}
        >
          {count}
        </div>
        <div className="mt-1 text-[11.5px] leading-none text-muted-foreground">{label}</div>
      </div>
    </button>
  );
}

/**
 * The workbench header's compact KPI strip — one card, dividers between items — replacing
 * the old three stat cards. Same data and permission gating as before: each KPI is hidden
 * without view rights on its module and clicks through to its page.
 */
export function DashboardKpiStrip({ ownerUserId }) {
  // Reactive permissions: a mid-session grant/revoke re-evaluates the strip.
  usePermissions();

  const [needsCount, setNeedsCount] = useState(() => Number(getThreadsNeedsCount()) || 0);
  useEffect(
    () =>
      subscribeThreadsNeedsCount((count) => {
        if (!ownerMatches(ownerUserId)) return;
        setNeedsCount(Number(count) || 0);
      }),
    [ownerUserId],
  );

  const [chatsUnread, setChatsUnread] = useState(() => Number(getChatsUnreadThreadsCount()) || 0);
  useEffect(
    () =>
      subscribeChatsUnread((count) => {
        if (!ownerMatches(ownerUserId)) return;
        setChatsUnread(Number(count) || 0);
      }),
    [ownerUserId],
  );

  const [myTasks, setMyTasks] = useState(() => Number(getTaskCounts()?.mine) || 0);
  const [myOverdue, setMyOverdue] = useState(() => Number(getTaskCounts()?.myOverdue) || 0);
  useEffect(
    () =>
      subscribeTaskCounts((counts) => {
        if (!ownerMatches(ownerUserId)) return;
        setMyTasks(Number(counts?.mine) || 0);
        setMyOverdue(Number(counts?.myOverdue) || 0);
      }),
    [ownerUserId],
  );

  const kpis = [
    hasPermission("messages", "view") && {
      key: "needs",
      icon: MessagesSquare,
      label: "Need you",
      count: needsCount,
      alert: needsCount > 0,
      page: "Messages",
    },
    hasPermission("chats", "view") && {
      key: "chats",
      icon: UsersRound,
      label: "Unread chats",
      count: chatsUnread,
      alert: chatsUnread > 0,
      page: "Chats",
    },
    hasPermission("tasks", "view") && {
      key: "tasks",
      icon: ListChecks,
      label: "My tasks",
      count: myTasks,
      alert: myOverdue > 0,
      page: "Tasks",
    },
  ].filter(Boolean);

  if (kpis.length === 0) {
    return null;
  }
  return (
    <Card className="flex flex-none items-center gap-[26px] rounded-[14px] px-6 py-3.5">
      {kpis.map((kpi, index) => (
        <div key={kpi.key} className="flex items-center gap-[26px]">
          {index > 0 && <div className="h-[30px] w-px bg-border" aria-hidden />}
          <KpiItem {...kpi} />
        </div>
      ))}
    </Card>
  );
}
